import { lazy } from 'react';

// lazy loading wrapper
import Loadable from '../components/Loadable';

// layouts
import DashboardLayout from '../layout/Dashboard';
import AuthLayout from '../layout/Auth';
import RouteError from '@common/error-boundary/RouteError';
// protected route
import ProtectedRoute from '../auth/ProtectedRoute';
import PermissionRoute from '../auth/PermissionRoute';
import ModuleRoute from '../auth/ModuleRoute';
import Calendar from '../views/calendar/Calendar';
import ViewAllNotifications from '../views/notification/ViewAllNotifications';
import PublicRoute from '../auth/PublicRoute';
// dashboard page
const DashboardHome = Loadable(lazy(() => import('../views/dashboard')));

// auth pages
const LoginPage = Loadable(lazy(() => import('../views/auth/login/Login')));
const RegisterPage = Loadable(lazy(() => import('../views/auth/register/Register')));
const HandoffPage = Loadable(lazy(() => import('../views/auth/HandoffPage')));
const ForgotPasswordPage = Loadable(lazy(() => import('../views/auth/forgotPassword/Forgot')));
const RolesPermissions = Loadable(lazy(() => import('../views/roles/RolesPermissions')));
const Settings = Loadable(lazy(() => import('../views/settings/Settings')));
const Restaurant = Loadable(lazy(() => import('../views/restaurant/Restaurant')));
const AddRestaurant = Loadable(lazy(() => import('../views/restaurant/components/AddRestaurant')));
const ViewRestaurant = Loadable(lazy(() => import('../views/restaurant/components/ViewRestaurantPage')));
const User = Loadable(lazy(() => import('../views/user/User')));
const AddUser = Loadable(lazy(() => import('../views/user/components/AddUser')));
const ViewUser = Loadable(lazy(() => import('../views/user/components/ViewUsersPage')));
const PreferenceAmenities = Loadable(lazy(() => import('../views/preferences/PreferenceAmenities')));
const CustomSetup = Loadable(lazy(() => import('../views/customsetup/CustomSetup')));
const AddServicePeriod = Loadable(lazy(() => import('../views/customsetup/components/Service/AddServicePeriod')));
const BookingManagement = Loadable(lazy(() => import('../views/bookingmanagement/BookingManagement')));
const AddNewBooking = Loadable(lazy(() => import('../views/bookingmanagement/components/AddBooking/AddNewBooking')));
const PublicBooking = Loadable(lazy(() => import('../views/public/Booking/PublicBookingWrapper')));
const HomeRedirect = Loadable(lazy(() => import('../views/landing/HomeRedirect')));
const GiftCard = Loadable(lazy(() => import('../views/GiftCard/GiftTicketContainer')));
const AddGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/GiftCard/pages/AddGiftCardPage')));
const EditGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/GiftCard/pages/EditGiftCardPage')));
const PublicGiftCardView = Loadable(lazy(() => import('../views/public/GiftCards/PublicGiftCardView')));
const ScanGiftCard = Loadable(lazy(() => import('../views/GiftCard/component/ScanGiftCard/ScanGiftCard')));
const PaymentResult = Loadable(lazy(() => import('../views/payement/PaymentResult')));
const PublicGiftCardUserContainer = Loadable(
  lazy(() => import('../views/public/GiftCards/PublicGiftCardUserContainer/PublicGiftCardUserContainer'))
);
const TicketEvent = Loadable(lazy(() => import('../views/ticketsAndEvents/TicketEventContainer')));
const AddEvent = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/AddEventPage/AddEventPage')));
const EditEvent = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/EditEventPage/EditEventPage')));
const NotFoundPage = Loadable(lazy(() => import('../views/notfound/NotFound')));
const TicketBookingPage = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/TicketBookingPage/TicketBookingPage')));
const BookingPaymentResultPage = Loadable(
  lazy(() => import('../views/ticketsAndEvents/pages/BookingPaymentResultPage/BookingPaymentResultPage'))
);
const BookingConfirmationPage = Loadable(
  lazy(() => import('../views/ticketsAndEvents/pages/BookingConfirmationPage/BookingConfirmationPage'))
);
const TicketScanPage = Loadable(lazy(() => import('../views/ticketsAndEvents/pages/TicketScanPage/TicketScanPage')));
const PublicTicketWrapper = Loadable(lazy(() => import('../views/public/Tickets/PublicTicketWrapper')));
const EventDetailPage = Loadable(lazy(() => import('../views/public/Tickets/EventDetailPage/EventDetailPage')));
const VoucherContainer = Loadable(lazy(() => import('../views/Vouchers/VoucherContainer')));
const VoucherBatchDetail = Loadable(lazy(() => import('../views/Vouchers/components/VoucherBatch/VoucherBatchDetail')));
const PublicVoucherWrapper = Loadable(lazy(() => import('../views/public/Vouchers/PublicVoucherWrapper')));
const PublicVoucherClaimWrapper = Loadable(lazy(() => import('../views/public/Vouchers/PublicVoucherClaimWrapper')));
const PublicVoucherPassPage = Loadable(lazy(() => import('../views/public/Vouchers/PublicVoucherPassPage')));
const LegalPage = Loadable(lazy(() => import('../views/public/Legal/LegalPage')));
const GuestPortal = Loadable(lazy(() => import('../views/guest/GuestPortal')));
const PublicPosInvoiceView = Loadable(lazy(() => import('../views/public/PosInvoice/PublicPosInvoiceView')));
const PublicPosTicketInvoiceView = Loadable(lazy(() => import('../views/public/PosTicketInvoice/PublicPosTicketInvoiceView')));
const MenusPage = Loadable(lazy(() => import('../views/Menus/Menus')));
const AddMenuPage = Loadable(lazy(() => import('../views/Menus/pages/AddMenuPage/AddMenuPage')));
const EditMenuPage = Loadable(lazy(() => import('../views/Menus/pages/EditMenuPage/EditMenuPage')));
const PosSetupPage = Loadable(lazy(() => import('../views/pos/PosSetupPage')));
const MerchantAgreementPage = Loadable(lazy(() => import('../views/merchantAgreement/MerchantAgreementPage')));
const StoreRoom = Loadable(lazy(() => import('../views/storeRoom/StoreRoom')));
const OnboardingPage = Loadable(lazy(() => import('../views/onboarding/OnboardingPage')));
const Loyalty = Loadable(lazy(() => import('../views/loyalty/Loyalty')));
const CompleteSetupPage = Loadable(lazy(() => import('../views/auth/completeSetup/CompleteSetup')));

const PagesRoutes = {
  path: '/',
  errorElement: <RouteError />,
  children: [
    {
      path: '/',
      element: <HomeRedirect />
    },

    {
      element: <AuthLayout />,
      children: [
        {
          path: 'login',
          element: (
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          )
        },
        {
          path: 'signup',
          element: (
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          )
        },
        {
          path: 'forgot-password',
          element: (
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          )
        },
        {
          path: 'payment/result/:transid',
          element: <PaymentResult />
        },
        {
          path: 'complete-setup/:token',
          element: <CompleteSetupPage />
        },
        {
          path: 'booking/payment-result',
          element: <BookingPaymentResultPage />
        },
        {
          path: 'booking/confirmation/:bookingRef',
          element: <BookingConfirmationPage />
        },
        { path: '*', element: <NotFoundPage /> }
      ]
    },

    {
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      errorElement: <RouteError />,
      children: [
        {
          path: 'dashboard',
          element: (
            <ModuleRoute module="dashboard">
              <PermissionRoute perm="dashboard_access">
                <DashboardHome />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'roles-permissions',
          element: (
            <ModuleRoute module="role_permission_management">
              <PermissionRoute perm="role_permission_access">
                <RolesPermissions />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'settings',
          element: (
            <ModuleRoute module="settings">
              <Settings />
            </ModuleRoute>
          )
        },
        {
          path: 'restaurant',
          element: (
            <ModuleRoute module="restaurant_management">
              <PermissionRoute perm="restaurant_listing">
                <Restaurant />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        { path: 'restaurant/add', element: <AddRestaurant /> },
        { path: 'restaurant/view/:id', element: <ViewRestaurant /> },

        {
          path: 'users',
          element: (
            <ModuleRoute module="user_management">
              <PermissionRoute perm="user_listing">
                <User />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        { path: 'users/add', element: <AddUser /> },
        { path: 'users/view/:id', element: <ViewUser /> },

        {
          path: 'facilities-preference',
          element: (
            <ModuleRoute module="preference_amenities">
              <PermissionRoute perm="preference_listing">
                <PreferenceAmenities />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'custom-setup',
          element: (
            <ModuleRoute module="custom_setup">
              <PermissionRoute perm="custom_setup_listing">
                <CustomSetup />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'custom-setup/serviceperiod/add',
          element: (
            <ModuleRoute module="custom_setup">
              <PermissionRoute perm="custom_setup_listing">
                <AddServicePeriod />
              </PermissionRoute>
            </ModuleRoute>
          )
        },

        {
          path: 'booking-management',
          element: (
            <ModuleRoute module="booking_management">
              <PermissionRoute perm="booking_listing">
                <BookingManagement />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'booking-management/new-booking',
          element: (
            <ModuleRoute module="booking_management">
              <PermissionRoute perm="booking_add">
                <AddNewBooking />
              </PermissionRoute>
            </ModuleRoute>
          )
        },

        {
          path: 'calendar',
          element: (
            <ModuleRoute module="calendar">
              <Calendar />
            </ModuleRoute>
          )
        },
        { path: 'notifications', element: <ViewAllNotifications /> },
        {
          path: 'gift_card',
          element: (
            <ModuleRoute module="gift_cards">
              <PermissionRoute perm="giftcard_listing">
                <GiftCard />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'gift_card/add',
          element: (
            <ModuleRoute module="gift_cards">
              <PermissionRoute perm="giftcard_add">
                <AddGiftCard />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'gift_card/edit/:id',
          element: (
            <ModuleRoute module="gift_cards">
              <PermissionRoute perm="giftcard_edit">
                <EditGiftCard />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'gift_card/scan',
          element: (
            <ModuleRoute module="gift_cards">
              <ScanGiftCard />
            </ModuleRoute>
          )
        },
        {
          // Hosts both the Event and Ticket tabs, so it gates per-tab inside
          // rather than on a single module here.
          path: 'ticket',
          element: <TicketEvent />
        },
        {
          path: 'event/add',
          element: (
            <ModuleRoute module="events">
              <PermissionRoute perm="event_add">
                <AddEvent />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'event/edit/:id',
          element: (
            <ModuleRoute module="events">
              <PermissionRoute perm="event_edit">
                <EditEvent />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: '/ticket/add',
          element: (
            <ModuleRoute module="tickets">
              <PermissionRoute perm="ticket_book">
                <TicketBookingPage />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'ticket/scan',
          element: (
            <ModuleRoute module="tickets">
              <PermissionRoute perm="ticket_scan">
                <TicketScanPage />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'menus',
          element: (
            <ModuleRoute module="menus">
              <PermissionRoute perm="menu_listing">
                <MenusPage />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'menus/add',
          element: (
            <ModuleRoute module="menus">
              <PermissionRoute perm="menu_add">
                <AddMenuPage />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'menus/edit/:id',
          element: (
            <ModuleRoute module="menus">
              <PermissionRoute perm="menu_edit">
                <EditMenuPage />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'pos-setup',
          element: (
            <ModuleRoute module="pos">
              <PermissionRoute perm="pos_setup">
                <PosSetupPage />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'loyalty',
          element: (
            <ModuleRoute module="loyalty">
              <PermissionRoute perm="loyalty_access">
                <Loyalty />
              </PermissionRoute>
            </ModuleRoute>
          )
        },
        {
          path: 'vouchers',
          element: (
            <ModuleRoute module="vouchers">
              <VoucherContainer />
            </ModuleRoute>
          )
        },
        {
          path: 'vouchers/batch/:id',
          element: (
            <ModuleRoute module="vouchers">
              <VoucherBatchDetail />
            </ModuleRoute>
          )
        },
        { path: 'merchant-agreement', element: <MerchantAgreementPage /> },
        // 404 inside protected area
        { path: '*', element: <NotFoundPage /> }
      ]
    },

    {
      // Onboarding: the business details a merchant gives before the product
      // opens. Full screen on purpose — there is nothing to navigate to yet,
      // and the drawer would offer pages the API is still refusing. Outside
      // DashboardLayout for that reason, and ProtectedRoute lets this one path
      // through rather than redirecting it back to itself.
      path: 'onboarding',
      errorElement: <RouteError />,
      element: (
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      )
    },
    {
      // The store room takes the whole screen: no drawer, no header, its own
      // back button. Authenticated like everything above, but deliberately NOT
      // nested under DashboardLayout, which renders the chrome unconditionally
      // and gives a child no way to opt out. ProtectedRoute is layout-free, so
      // it can carry the auth check on its own.
      path: 'store-room',
      errorElement: <RouteError />,
      element: (
        <ProtectedRoute>
          <ModuleRoute module="store_room">
            <PermissionRoute perm="store_room_access">
              <StoreRoom />
            </PermissionRoute>
          </ModuleRoute>
        </ProtectedRoute>
      )
    },
    {
      // Mobile → web sign-in handoff. Deliberately NOT wrapped in PublicRoute:
      // that redirects away when a token already exists, which would block a
      // handoff meant to switch/refresh the session.
      path: 'auth/handoff',
      element: <HandoffPage />
    },
    {
      path: 'gift-card/view/:code',
      element: <PublicGiftCardView />
    },
    {
      path: 'gift-card/view',
      element: <PublicGiftCardView />
    },
    {
      // The guest rewards wallet. Declared above ':restaurantId' for
      // readability rather than necessity — a static segment already outranks
      // a dynamic one — but a restaurant whose slug is "rewards" would
      // otherwise be one rename away from a collision nobody would look for.
      //
      // Not wrapped in PublicRoute: that redirects away when a merchant token
      // exists, and a guest opening their own wallet on a laptop where someone
      // is signed in to the merchant app is a guest, not a merchant.
      path: 'rewards',
      element: <GuestPortal />
    },
    {
      path: ':restaurantId',
      element: <PublicBooking />
    },
    {
      path: 'gift/:restaurantId',
      element: <PublicGiftCardUserContainer />
    },
    {
      path: 'tickets/:slug',
      element: <PublicTicketWrapper />
    },
    {
      path: 'tickets/:slug/event/:eventId',
      element: <EventDetailPage />
    },
    {
      path: 'voucher/pass/:token',
      element: <PublicVoucherPassPage />
    },
    {
      path: 'voucher/validate/:slug',
      element: <PublicVoucherWrapper />
    },
    {
      path: 'voucher/:slug',
      element: <PublicVoucherClaimWrapper />
    },
    {
      path: 'pos-invoice/:token',
      element: <PublicPosInvoiceView />
    },
    {
      path: 'pos-ticket-invoice/:token',
      element: <PublicPosTicketInvoiceView />
    },
    {
      path: 'terms',
      element: <LegalPage type="terms_of_service" />
    },
    {
      path: 'privacy-policy',
      element: <LegalPage type="privacy_policy" />
    }
  ]
};

export default PagesRoutes;
